<template>
  <v-row no-gutters>
    <v-col cols="12">
      <app-ag-grid
        title="Invoices"
        :columnDefs="columnDefs"
        :rowData="invoices"
        :loading="loading"
        @refresh="fetchInvoices"
        @add="onAdd"
        :getRowStyle="getRowStyle"
        :mobileCard="mobileCard"
        @row-dbl-click="
          (data) => $router.push({ path: `invoices/${data._id}/edit` })
        "
      />
    </v-col>
  </v-row>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";

export default {
  name: "InvoicesPage",
  components: {
    AppAgGrid,
  },

  data() {
    return {
      loading: false,
      columnDefs: [
        {
          headerName: "View",
          field: "_id",
          width: 80,
          cellRenderer: (params) => {
            return `
              <i
                class="mdi mdi-eye view-invoice"
                data-id="${params.data._id}"
                title="View Invoice"
                style="
                  cursor: pointer;
                  font-size: 18px;
                  color: #0a5a90;
                "
              ></i>
            `;
          },

          onCellClicked: (params) => {
            if (params.event.target.classList.contains("view-invoice")) {
              this.$router.push({ path: `invoices/${params.data._id}/view` });
            }
          },
        },
        { headerName: "Invoice Number", field: "invoiceNumber", width: 130 },
        { headerName: "Customer", field: "customer.name", width: 150 },
        {
          headerName: "Subtotal",
          field: "subtotal",
          valueFormatter: (p) => {
            if (p.value == null) return "-";

            return new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
            }).format(p.value);
          },
          width: 150,
        },
        {
          headerName: "GST",
          field: "gst",
          valueFormatter: (p) => {
            if (p.value == null) return "-";

            return new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
            }).format(p.value);
          },
          width: 150,
        },
        {
          headerName: "Amount",
          field: "totalAmount",
          valueFormatter: (p) => {
            if (p.value == null) return "-";

            return new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
            }).format(p.value);
          },
          width: 150,
        },

        { headerName: "Status", field: "status", width: 120 },

        {
          headerName: "Due Date",
          field: "dueDate",
          valueFormatter: (p) => {
            if (!p.value) return "-";

            const date = new Date(p.value);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();

            return `${day}-${month}-${year}`;
          },
          width: 150,
        },

        {
          headerName: "Created",
          field: "createdAt",
          valueFormatter: (p) => {
            if (!p.value) return "-";

            const date = new Date(p.value);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();

            return `${day}-${month}-${year}`;
          },
          width: 150,
        },

        { headerName: "Note", field: "note", width: 250 },

        { headerName: "User", field: "userId.name", width: 200 },
        { headerName: "Email", field: "userId.email", width: 300 },
      ],
    };
  },

  computed: {
    invoices() {
      return this.$store.state.invoices.list || [];
    },

    mobileCard() {
      const statusColors = {
        PAID: { background: "#e6f7ec", color: "#15803d" },
        PARTIALLY_PAID: { background: "#e6f0fb", color: "#0a5a90" },
        PENDING: { background: "#fff7e6", color: "#b8720a" },
        CANCELLED: { background: "#fdecea", color: "#c62828" },
      };

      return {
        title: (row) => `#${row.invoiceNumber}`,
        subtitle: (row) => row.customer?.name || "-",
        badge: (row) => ({
          text: row.status || "-",
          style: statusColors[row.status] || { background: "#f1f5f9", color: "#475569" },
        }),
        fields: (row) => [
          { label: "Amount", value: this.formatCurrency(row.totalAmount) },
          { label: "Due", value: this.formatDate(row.dueDate) },
        ],
        actions: (row) => [
          {
            label: "View",
            icon: "mdi-eye-outline",
            color: "#0a5a90",
            onClick: () => this.$router.push({ path: `invoices/${row._id}/view` }),
          },
          {
            label: "Edit",
            icon: "mdi-pencil-outline",
            color: "#475569",
            onClick: () => this.$router.push({ path: `invoices/${row._id}/edit` }),
          },
        ],
      };
    },
  },

  created() {
    this.fetchInvoices();
  },

  methods: {
    async fetchInvoices() {
      this.loading = true;
      try {
        await this.$store.dispatch("invoices/getInvoices");
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    onAdd() {
      this.$router.push({ path: "invoices/create" });
    },

    formatCurrency(value) {
      if (value == null) return "-";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(value);
    },

    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    getRowStyle(params) {
      if (!params.data || !params.data.dueDate) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const due = new Date(params.data.dueDate);
      due.setHours(0, 0, 0, 0);

      const isOverdue = due < today;
      const isPaid = params.data.status === "PAID";

      if (isOverdue && !isPaid) {
        return {
          background: "#f44336",
        };
      }
    },
  },
};
</script>
